// CalendarView.tsx — Root des Kalender-Neubaus (`/termine`). Mountet den
// CalendarStateProvider und wählt über useResponsive zwischen Desktop- und
// Mobile-Shell. CSS-Scope `.cal-rd` auf der Root (NICHT .kvp-rd), damit SP6
// nicht nachscopen muss.
//
// SP1-Umfang: Desktop-Shell = Top-Leiste (CalendarToolbar) → Nav-Zeile
// (CalendarNavBar) → TimeGridView (events=[]). Mobile = minimaler Platzhalter,
// der nur TimeGridView rendert (Vollausbau Tages-Selektor/FAB/BottomSheet = SP6).
// SP2: Event-Klick öffnet das Termin-Fenster (Edit).
// SP3: leerer Slot ziehen/klicken → Drag-to-Create (openTerminFormWindow mit
// Zeit-Prefill, WRITE-gate canCreateInMailbox); Termin ziehen/resizen →
// Reschedule-Persistenz via calendarService.updateEvent (Konsistenz-Review
// C5/B1 — eindeutig SP3). Nach erfolgreichem Reschedule requestCalendarReload().
// Termine-SP4 (Review 2026-07-05, A14/A16): Einzelklick → Peek-Popover
// (TerminPeekPopover), Doppelklick → direkt das Termin-Fenster, Rechtsklick →
// Kontextmenü („Verschieben"-Quick-Reschedule + „Teams-Meeting teilnehmen").

import React, { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { useResponsive } from "../../hooks/useResponsive";
import { REFINED } from "../risikoanalyseStep/tokens";
import { useWindowManager } from "../windowManager/WindowManagerContext";
import { openTerminFormWindow, type TerminPrefill } from "../windowManager/openTerminFormWindow";
import { useMailContext } from "../../context/MailContext";
import { useAuth } from "../../context/AuthContext";
import { calendarService } from "../../services/calendarService";
import { resolveDefaultCalendarMailbox } from "../../utils/mailboxAccess";
import type { KalenderEventChange } from "../OutlookKalender";
import {
    CalendarStateProvider,
    requestCalendarReload,
    useCalendarState,
    type CalendarView as CalendarViewName,
} from "./CalendarStateContext";
import CalendarToolbar from "./CalendarToolbar";
import CalendarNavBar from "./CalendarNavBar";
import TimeGridView from "./TimeGridView";
import SharedColumnsView, { type SharedColumn } from "./SharedColumnsView";
import MobileCalendarShell from "./MobileCalendarShell";
import TerminPeekPopover, { peekAnchorFromDom, type TerminPeek } from "./TerminPeekPopover";
import TerminContextMenu, { type TerminCtxMenu } from "./TerminContextMenu";
import { HinweisToast, useHinweisToast } from "./HinweisToast";
import { inklusivesAllDayEnde } from "./allDay";
import { kannDuplizieren } from "./terminVorlage";
import { naiveIso } from "./peek";
import type { CalendarGridEvent } from "./types";
import { auswahlAusGridEvent, type TerminAuswahl } from "./calendarAuswahl";
import CalendarModalKopf from "./CalendarModalKopf";
import type { OpenTerminFormParams } from "../windowManager/openTerminFormWindow";
import { hinweisen } from "../redesign/bestaetigen";

/**
 * K6 — der Kalender ist seit 23.08.2026 nicht mehr nur die Seite `/termine`,
 * sondern ein wiederverwendbarer Baustein: dieselbe Komposition läuft als
 * FENSTER über der Mail-Leiste (J3) und über dem Termine-Reiter des Vorgangs
 * (K7). Ohne Props verhält sie sich exakt wie bisher — die Route bleibt
 * unverändert propfrei.
 */
export interface CalendarViewProps {
    /** 'seite' = Route /termine (Default), 'auswahl' = Verknüpfen-Fenster. */
    modus?: "seite" | "auswahl";
    /** Auswahlmodus: ein Klick auf einen Terminblock liefert ihn hier ab. */
    onPick?: (t: TerminAuswahl) => void | Promise<void>;
    /** Bereits verknüpfte Termine — bekommen ein Häkchen. */
    bereitsVerknuepft?: { mailbox: string; graphEventId: string }[];
    /** Auswahl auf diese Postfächer beschränken (Default: alle lesbaren). */
    postfaecher?: string[];
    /** Kalenderauswahl/View lokal halten (Default: im Auswahlmodus an). */
    auswahlIsoliert?: boolean;
    initialDate?: Date;
    initialView?: CalendarViewName;
    /** Vorbelegung für „+ Neuer Termin" — z. B. linkVorgangId oder linkMail. */
    neuTerminPrefill?: Partial<OpenTerminFormParams>;
    /** Deep-Link-Auswertung (?date/?event) — Default: nur im Seiten-Modus. */
    deepLink?: boolean;
    /** Kopfzeile des Auswahlmodus. */
    titel?: string;
    hinweis?: string;
    /** Fenster-Id: CalendarView schließt sich darüber selbst („Fertig"). */
    windowId?: string;
    /**
     * Der Kalender steckt in einem Vollflächen-Overlay (`KalenderOverlay`), das
     * Titel, Hinweis, „Fertig" und Schließkreuz selbst stellt. Dann entfällt die
     * eigene Kopfzeile des Auswahlmodus — sonst stünden zwei Köpfe übereinander.
     */
    imOverlay?: boolean;
}

/** Die an BEIDE Shells durchgereichten Props (Desktop UND Telefon). */
export interface CalendarShellProps {
    modus: "seite" | "auswahl";
    onPick?: (t: TerminAuswahl) => void | Promise<void>;
    bereitsVerknuepft?: { mailbox: string; graphEventId: string }[];
    neuTerminPrefill?: Partial<OpenTerminFormParams>;
    titel?: string;
    hinweis?: string;
    windowId?: string;
    /** Hülle ist ein Vollflächen-Overlay → eigene Kopfzeile weglassen. */
    imOverlay?: boolean;
}

/** CalendarGridEvent (SP1) → Minimal-Prefill für das Termin-Fenster. */
const toPrefill = (e: CalendarGridEvent): TerminPrefill => ({
    id: e.id,
    title: e.title,
    start: e.start,
    end: e.end,
    allDay: e.allDay,
    location: e.location,
    mailbox: e.mailbox,
});

// naive-lokale ISO-Formatierung: seit SP4 single-sourced in peek.ts (naiveIso).

/**
 * Termine-SP4 — Lade-Feedback über dem Grid:
 *  - `initialLoading` (kalter Erst-Load, leeres Grid): deckender Spinner-Overlay,
 *    damit kein leeres Raster kurz aufblitzt.
 *  - `revalidating` (Cache-Treffer/sichtbare Events, Hintergrund-Refresh): NUR
 *    ein dezenter „Aktualisiere…"-Pill oben rechts — kein Grid-Leeren/Flackern.
 * Beide Zustände schließen sich im Context gegenseitig aus. Die Klassen liegen in
 * calendar.css (`.cal-rd`-Scope); der Pill ist klick-transparent.
 */
const CalendarLoadingIndicator: React.FC<{ initialLoading: boolean; revalidating: boolean }> = ({
    initialLoading,
    revalidating,
}) => {
    if (initialLoading) {
        return (
            <div className="cal-load-overlay" role="status" aria-live="polite">
                <span className="cal-load-spinner" aria-hidden />
                <span className="cal-load-text">Termine werden geladen…</span>
            </div>
        );
    }
    if (revalidating) {
        return (
            <div className="cal-reval-pill" role="status" aria-live="polite">
                <span className="cal-reval-spinner" aria-hidden />
                Aktualisiere…
            </div>
        );
    }
    return null;
};

/**
 * Gemeinsame Kalender-Aktionen (Desktop + Mobile teilen sie): WRITE-Gating,
 * Event-Klick→Edit, Drag-to-Create→openTerminFormWindow, Drag/Resize→Persistenz.
 * SP4: Einzelklick → Peek-Popover, Doppelklick → Fenster, Rechtsklick → Menü.
 */
function useCalendarActions(shell: CalendarShellProps) {
    const { openWindow, closeWindow } = useWindowManager();
    const auswahl = shell.modus === "auswahl";
    // `currentMailbox` wird hier seit dem 22.09.2026 nicht mehr gebraucht: Die
    // Neuanlage richtet sich nach dem eigenen Postfach, nicht nach dem im
    // Mail-Modul gewählten.
    const { mailboxes } = useMailContext();
    const { user } = useAuth();
    // Eigene Login-Adresse als Fallback für die Eigen-Postfach-Erkennung, falls
    // das Backend-Flag `isOwn` (PR #891) an einem Postfach fehlt (Alt-API).
    const ownAddress = (user?.email as string | undefined) ?? null;
    // SP2: sichtbares Feedback statt stillem No-Op (kein WRITE-Kalender vorhanden).
    const { hinweis, zeigeHinweis } = useHinweisToast();

    // WRITE-Gating je Postfach über das echte calendarAccess (SP5, Konsistenz
    // B1 — ersetzt den SP3-MVP-Vergleich mailbox===currentMailbox). Deckt auch
    // geteilte WRITE-Postfächer ab. Doppelt durch assertCalendarWrite im Backend.
    const canWriteMailbox = useCallback(
        (mailbox: string | null | undefined) => {
            if (!mailbox) return false;
            const mb = mailboxes.find(
                (m) => m.mailboxAddress.toLowerCase() === mailbox.toLowerCase(),
            );
            return mb?.calendarAccess === "WRITE";
        },
        [mailboxes],
    );

    // WRITE-Gating je Event. SP4: Serieninstanzen sind NICHT drag-/resize-bar
    // (Entscheidung D) — ein Instanz-Drag müsste eine Graph-Exception erzeugen,
    // die der MVP nicht behandelt. Master/Einzeltermin bleiben editierbar.
    const canEditEvent = useCallback(
        (e: CalendarGridEvent) =>
            canWriteMailbox(e.mailbox) &&
            !(e.type === "occurrence" || e.type === "exception"),
        [canWriteMailbox],
    );

    // Ein eigenes WRITE-Gate für die Neuanlage gibt es seit dem 22.09.2026
    // nicht mehr: Die Anlage fragt nicht länger, ob das GERADE GEWÄHLTE
    // Postfach schreiben darf, sondern nimmt das eigene
    // (`resolveDefaultCalendarMailbox` liefert ohnehin nur WRITE-Postfächer).

    // K6: im Auswahlmodus trägt die Neuanlage die Vorbelegung des Aufrufers —
    // so hängt ein hier angelegter Termin sofort am Vorgang bzw. an der Mail.
    // KEIN `mailbox` mehr (PO 22.09.2026: „bei neuen Termin immer die eigene
    // Adresse verwenden"). Hier stand `currentMailbox` — das Postfach, das im
    // MAIL-Modul gewählt ist, also meist cbb@, weil dort die Vorgangs-Mails
    // hängen. Wer von dort in den Kalender wechselte und „Neuer Termin"
    // drückte, legte ihn still in einem fremden Kalender an. Ohne Angabe
    // greift die Vorauswahl des Dialogs: das eigene WRITE-Postfach
    // (`resolveCreateTargetMailbox`). Ändern lässt es sich weiterhin — jetzt
    // ganz oben im Formular statt unten in den Optionen.
    const onNewTermin = useCallback(() => {
        openTerminFormWindow(openWindow, { ...(shell.neuTerminPrefill ?? {}) });
    }, [openWindow, shell.neuTerminPrefill]);

    // SP4: Peek-Popover (Einzelklick) + Kontextmenü (Rechtsklick) — der State
    // lebt hier, gerendert wird in der DesktopShell (Mobile hat einen eigenen
    // BottomSheet-Pfad in MobileCalendarShell).
    const [peek, setPeek] = useState<TerminPeek | null>(null);
    const [ctxMenu, setCtxMenu] = useState<TerminCtxMenu | null>(null);
    const closePeek = useCallback(() => setPeek(null), []);
    const closeCtxMenu = useCallback(() => setCtxMenu(null), []);

    /** Volles Termin-Fenster öffnen (Peek-Aktion „Öffnen" + Doppelklick). */
    const onOpenEdit = useCallback(
        (e: CalendarGridEvent) => {
            openTerminFormWindow(openWindow, {
                editEventId: e.id,
                mailbox: e.mailbox,
                prefillEvent: toPrefill(e),
                canEdit: canEditEvent(e),
            });
        },
        [openWindow, canEditEvent],
    );

    /**
     * T-269 „Duplizieren…" (Rechtsklick): Neuanlage mit dem Termin als Vorlage.
     * Kein `mailbox` — das Duplikat landet wie jede Neuanlage im eigenen
     * Schreib-Postfach (PO 22.09.2026), nicht still im Kalender der Vorlage.
     * Die Vorbelegung des Auswahlmodus (Vorgang/Mail) gewinnt.
     */
    const onDuplizieren = useCallback(
        (e: CalendarGridEvent) => {
            openTerminFormWindow(openWindow, {
                initialStart: e.start,
                initialEnd: e.allDay ? inklusivesAllDayEnde(e.start, e.end) : e.end,
                allDay: e.allDay,
                initialSubject: e.title,
                vorlage: {
                    mailbox: e.mailbox,
                    eventId: e.id,
                    titel: e.title,
                    location: e.location,
                    ueberKontakt: !!e.ueberKontakt,
                },
                ...(shell.neuTerminPrefill ?? {}),
            });
        },
        [openWindow, shell.neuTerminPrefill],
    );
    /** Maskierte fremde private Termine taugen nicht als Vorlage. */
    const kannDuplizierenEvent = useCallback(
        (e: CalendarGridEvent) => {
            const adr = e.mailbox.toLowerCase();
            const eigen =
                mailboxes.some((m) => m.isOwn && m.mailboxAddress.toLowerCase() === adr) ||
                (ownAddress ?? "").toLowerCase() === adr;
            return kannDuplizieren(e, eigen);
        },
        [mailboxes, ownAddress],
    );

    // Einzelklick → Peek-Popover am Block (A16); ein offenes Menü schließt.
    // K6: im Auswahlmodus KEIN Peek — der Klick verknüpft sofort.
    const onSelectEvent = useCallback(
        (e: CalendarGridEvent, domEvent?: React.SyntheticEvent) => {
            setCtxMenu(null);
            if (auswahl && shell.onPick) {
                void shell.onPick(auswahlAusGridEvent(e));
                return;
            }
            setPeek({ event: e, anchor: peekAnchorFromDom(domEvent) });
        },
        [auswahl, shell.onPick],
    );

    // Doppelklick → weiterhin DIREKT das Termin-Fenster (A16).
    const onDoubleClickEvent = useCallback(
        (e: CalendarGridEvent) => {
            setPeek(null);
            setCtxMenu(null);
            onOpenEdit(e);
        },
        [onOpenEdit],
    );

    // Rechtsklick → Kontextmenü (A14); ein offener Peek schließt.
    const onEventContextMenu = useCallback(
        (e: CalendarGridEvent, pos: { x: number; y: number }) => {
            setPeek(null);
            setCtxMenu({ event: e, pos });
        },
        [],
    );

    // Drag-to-Create (SP3/SP2): leerer Slot (bereits zu {start,end,allDay}
    // normalisiert in TimeGridView) → Termin-Fenster mit Zeit-Prefill.
    // SP2 (Review 2026-07-07): KEIN stiller No-Op mehr, wenn kein Postfach
    // Kalender-Schreibrecht hat (das war die Ursache für „Selektion öffnet kein
    // Popup") — dann ein Toast statt eines stillen `return`.
    //
    // Bis zum 22.09.2026 gewann hier `currentMailbox`, sofern es Schreibrecht
    // hatte — also das im Mail-Modul gewählte Postfach, meist cbb@. Der PO:
    // „bei neuen Termin immer die eigene Adresse verwenden." Das eigene
    // Postfach gewinnt jetzt immer; die Prüfung bleibt nur, um den Toast bei
    // GAR keinem Schreibrecht auszulösen.
    const onSelectSlot = useCallback(
        (slot: { start: Date; end: Date; allDay: boolean }) => {
            const zielMailbox =
                resolveDefaultCalendarMailbox(mailboxes, ownAddress)?.mailboxAddress ?? null;
            if (!zielMailbox) {
                zeigeHinweis("Kein Kalender mit Schreibrecht — Termin kann nicht angelegt werden.");
                return;
            }
            // Ganztags-Selektion: das exklusive Spannen-Ende (Graph-Konvention)
            // auf den inklusiven letzten Tag zurückrechnen — TerminFormWindow
            // rechnet beim Speichern selbst wieder +1 (sonst 1 Tag zu lang).
            const endeFuersFormular = slot.allDay
                ? inklusivesAllDayEnde(slot.start, slot.end)
                : slot.end;
            openTerminFormWindow(openWindow, {
                initialStart: slot.start,
                initialEnd: endeFuersFormular,
                allDay: slot.allDay,
                // Kein `showMailboxHint` mehr: Das Postfach steht jetzt als
                // Auswahl ganz oben im Formular und braucht keinen Hinweistext,
                // der nur in einem Sonderfall erschien.
                mailbox: zielMailbox,
                ...(shell.neuTerminPrefill ?? {}),
            });
        },
        [openWindow, mailboxes, ownAddress, zeigeHinweis, shell.neuTerminPrefill],
    );

    // Reschedule-Persistenz (SP3, Konsistenz-Review C5/B1): Drag/Resize eines
    // eigenen Termins → PATCH über die bestehende calendarService.updateEvent-
    // Pipeline (naive-lokale ISO + timeZone:'Europe/Berlin', wie der Submit-/
    // Reschedule-Pfad). Doppelsicherung canEditEvent; bei Fehler Reload (in SP3
    // kein lokaler optimistischer State, da events erst in SP5 geladen werden →
    // requestCalendarReload zieht den Server-Stand). SP5 ergänzt optimistisches
    // Update + Rollback im Context.
    const handleEventChange = useCallback(
        async ({ event, start, end, isAllDay }: KalenderEventChange) => {
            const e = event as CalendarGridEvent;
            if (!canEditEvent(e)) return;
            const neuAllDay = isAllDay ?? e.allDay;
            try {
                await calendarService.updateEvent(e.mailbox, e.id, {
                    start: naiveIso(start),
                    end: naiveIso(end),
                    timeZone: "Europe/Berlin",
                    isAllDay: neuAllDay,
                });
            } catch (err) {
                console.error("[CalendarView] Umplanen fehlgeschlagen", err);
                void hinweisen({ titel: "Termin nicht verschoben", text: "Der Termin konnte nicht verschoben werden. Bitte versuche es noch einmal.", ton: "danger" });
            } finally {
                // Server-Stand neu ziehen (SP5 füllt reloadEvents; in SP3 No-Op).
                requestCalendarReload();
            }
        },
        [canEditEvent],
    );

    /** „Fertig" im Auswahlmodus — das Fenster schließt sich selbst. */
    const onFertig = useCallback(() => {
        if (shell.windowId) closeWindow(shell.windowId);
    }, [closeWindow, shell.windowId]);

    return {
        canEditEvent,
        onNewTermin,
        onOpenEdit,
        onDuplizieren,
        kannDuplizierenEvent,
        onSelectEvent,
        onDoubleClickEvent,
        onEventContextMenu,
        onSelectSlot,
        handleEventChange,
        peek,
        closePeek,
        ctxMenu,
        closeCtxMenu,
        hinweis,
        onFertig,
    };
}

/** Events nach Quell-Postfach gruppieren (address lowercased), für die geteilte
 *  Ansicht. Erwartet die UNgededupten filteredColumnEvents (Review-Fix zu #784):
 *  der Overlay-Dedupe (Entscheidung 62) gilt nur für die Einzel-/Overlay-Ansicht;
 *  hier zeigt jede Spalte ihr Postfach vollständig. */
function groupByColumn(events: CalendarGridEvent[]): Map<string, CalendarGridEvent[]> {
    const map = new Map<string, CalendarGridEvent[]>();
    for (const e of events) {
        const key = e.source.address.toLowerCase();
        const arr = map.get(key);
        if (arr) arr.push(e);
        else map.set(key, [e]);
    }
    return map;
}

const DesktopShell: React.FC<CalendarShellProps> = (shell) => {
    const {
        canEditEvent,
        onNewTermin,
        onOpenEdit,
        onDuplizieren,
        kannDuplizierenEvent,
        onSelectEvent,
        onDoubleClickEvent,
        onEventContextMenu,
        onSelectSlot,
        handleEventChange,
        peek,
        closePeek,
        ctxMenu,
        closeCtxMenu,
        hinweis,
        onFertig,
    } = useCalendarActions(shell);
    const { calendarMailboxes } = useMailContext();
    const {
        filteredEvents,
        filteredColumnEvents,
        shared,
        calendarSources,
        currentDate,
        initialLoading,
        revalidating,
    } = useCalendarState();

    // Geteilte Ansicht nur bei aktivem Toggle UND >1 gewähltem Kalender.
    const useShared = shared && calendarSources.length > 1;

    // Spalten der geteilten Ansicht (Quelle + echtes WRITE-Recht je Spalte, B1).
    const columns = useMemo<SharedColumn[]>(
        () =>
            calendarSources.map((src) => {
                // T-69: Kalender-Liste — Kollegen-/Raumkalender ohne Grant
                // stehen dort mit READ, ihre Spalte ist also nur lesbar.
                const mb = (calendarMailboxes ?? []).find(
                    (m) => m.mailboxAddress.toLowerCase() === src.address.toLowerCase(),
                );
                return {
                    address: src.address,
                    displayName: src.displayName,
                    color: src.color,
                    canWrite: mb?.calendarAccess === "WRITE",
                };
            }),
        [calendarSources, calendarMailboxes],
    );
    // UNgededupte Events je Spalte (Review-Fix zu #784) — nie filteredEvents
    // hier verwenden, sonst verschwinden Termine aus den Verlierer-Spalten.
    const eventsByColumn = useMemo(
        () => groupByColumn(filteredColumnEvents),
        [filteredColumnEvents],
    );

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                minHeight: 0,
                background: REFINED.white,
            }}
        >
            {shell.modus === "auswahl" && !shell.imOverlay && (
                <CalendarModalKopf
                    titel={shell.titel ?? "Termin verknüpfen"}
                    hinweis={shell.hinweis}
                    onFertig={shell.windowId ? onFertig : undefined}
                />
            )}
            <CalendarToolbar onNewTermin={onNewTermin} />
            <CalendarNavBar />
            <div
                style={{
                    flex: 1,
                    minHeight: 0,
                    padding: 12,
                    boxSizing: "border-box",
                    position: "relative",
                }}
            >
                {useShared ? (
                    // SP5: N Kalender-Spalten nebeneinander (Tagesansicht je Spalte).
                    <SharedColumnsView
                        columns={columns}
                        date={currentDate}
                        eventsByColumn={eventsByColumn}
                        onSelectEvent={onSelectEvent}
                        onDoubleClickEvent={onDoubleClickEvent}
                        onEventContextMenu={onEventContextMenu}
                        onEventDrop={handleEventChange}
                        onEventResize={handleEventChange}
                    />
                ) : (
                    // SP5: einspaltiges Grid mit gefilterten, real geladenen Events.
                    <TimeGridView
                        events={filteredEvents}
                        onSelectEvent={onSelectEvent}
                        onDoubleClickEvent={onDoubleClickEvent}
                        onEventContextMenu={onEventContextMenu}
                        onSelectSlot={onSelectSlot}
                        onEventDrop={handleEventChange}
                        onEventResize={handleEventChange}
                        canEditEvent={canEditEvent}
                    />
                )}
                {/* SP4: Skeleton beim kalten Erst-Load, dezenter Pill bei
                    Hintergrund-Revalidierung (deckt Overlay- UND geteilte Ansicht). */}
                <CalendarLoadingIndicator
                    initialLoading={initialLoading}
                    revalidating={revalidating}
                />
            </div>
            {/* SP4: Einzelklick-Peek (A16) + Rechtsklick-Menü (A14). */}
            <TerminPeekPopover
                peek={peek}
                onClose={closePeek}
                canEditEvent={canEditEvent}
                onOpenEdit={onOpenEdit}
            />
            <TerminContextMenu
                menu={ctxMenu}
                onClose={closeCtxMenu}
                canEditEvent={canEditEvent}
                onDuplizieren={onDuplizieren}
                kannDuplizieren={kannDuplizierenEvent}
            />
            {/* SP2: sichtbarer Hinweis, wenn eine Anlage nicht möglich ist. */}
            <HinweisToast text={hinweis} />
        </div>
    );
};

/**
 * Deep-Link: /termine?date=YYYY-MM-DD springt auf diesen Tag (Tagesansicht,
 * globale Suche — unverändert). Zusätzlich (Dashboard-Termine-Review SP5, A10):
 * optionale Parameter &event=<id>&mailbox=<addr> aus dem Doppelklick der
 * Dashboard-Termine-Karte — nach dem Event-Load wird das Termin-Fenster geöffnet
 * (wie ein Klick im Kalender). Findet sich das Event nicht (z. B. fremdes
 * Postfach nicht in der Overlay-Auswahl), bleibt es beim Tagesansicht-Fallback —
 * still, kein Fehler-Toast. Muss INNERHALB des CalendarStateProvider liegen
 * (nutzt setCurrentDate/setView/events). Parameter werden danach aus der URL
 * entfernt.
 */
const CalendarDeepLink: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { setCurrentDate, setView, events } = useCalendarState();
    const { openWindow } = useWindowManager();
    const { mailboxes } = useMailContext();
    const handledRef = React.useRef<string>("");
    const [pendingEvent, setPendingEvent] = useState<{ id: string; mailbox: string } | null>(null);

    // WRITE-Gating je Event (repliziert useCalendarActions.canEditEvent): fremde
    // Kalender ohne WRITE sowie Serieninstanzen öffnen read-only.
    const canEditEvent = React.useCallback(
        (e: CalendarGridEvent): boolean => {
            const mb = mailboxes.find(
                (m) => m.mailboxAddress.toLowerCase() === e.mailbox.toLowerCase(),
            );
            return (
                mb?.calendarAccess === "WRITE" &&
                !(e.type === "occurrence" || e.type === "exception")
            );
        },
        [mailboxes],
    );

    // Param-Konsum: date → Tagesansicht; optional event/mailbox vormerken.
    React.useEffect(() => {
        const dateStr = searchParams.get("date");
        const eventId = searchParams.get("event");
        const mailboxParam = searchParams.get("mailbox");
        if (!dateStr && !eventId) return;
        const key = `${dateStr ?? ""}|${eventId ?? ""}|${mailboxParam ?? ""}`;
        if (handledRef.current === key) return;
        handledRef.current = key;

        if (dateStr) {
            const d = new Date(dateStr);
            if (!Number.isNaN(d.getTime())) {
                setCurrentDate(d);
                setView("day");
            }
        }
        // event/mailbox strikt optional: ohne event bleibt es beim reinen
        // Datum-Deep-Link (globale Suche, unverändert).
        setPendingEvent(eventId ? { id: eventId, mailbox: mailboxParam ?? "" } : null);

        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.delete("date");
                next.delete("event");
                next.delete("mailbox");
                return next;
            },
            { replace: true },
        );
    }, [searchParams, setCurrentDate, setView, setSearchParams]);

    // Nach dem Event-Load das passende Event suchen und das Termin-Fenster öffnen.
    React.useEffect(() => {
        if (!pendingEvent) return;
        const match = events.find(
            (e) =>
                e.id === pendingEvent.id &&
                (pendingEvent.mailbox.length === 0 ||
                    e.mailbox.toLowerCase() === pendingEvent.mailbox.toLowerCase()),
        );
        if (!match) return;
        setPendingEvent(null);
        openTerminFormWindow(openWindow, {
            editEventId: match.id,
            mailbox: match.mailbox,
            prefillEvent: toPrefill(match),
            canEdit: canEditEvent(match),
        });
    }, [pendingEvent, events, openWindow, canEditEvent]);

    // Sicherheits-Timeout: findet sich das Event nicht (fremdes Postfach nicht in
    // der Auswahl), Pending nach einigen Sekunden verwerfen — nur Tagesansicht,
    // kein hängender Zustand.
    React.useEffect(() => {
        if (!pendingEvent) return;
        const t = window.setTimeout(() => setPendingEvent(null), 8000);
        return () => window.clearTimeout(t);
    }, [pendingEvent]);

    return null;
};

const CalendarView: React.FC<CalendarViewProps> = (props) => {
    const { isPhone } = useResponsive();
    const modus = props.modus ?? "seite";
    // Der Deep-Link liest useSearchParams — im Fenster wäre das ein
    // Nebeneffekt auf die aktuelle Route. Deshalb standardmäßig nur auf /termine.
    const deepLink = props.deepLink ?? modus === "seite";
    // Im Auswahlmodus ist die Isolation Pflicht-Default: ein Häkchen im Fenster
    // darf die persistierte Auswahl der Seite /termine nicht umschreiben.
    const auswahlIsoliert = props.auswahlIsoliert ?? modus === "auswahl";

    // `postfaecher` beschränkt die STARTauswahl des isolierten Providers.
    const initialCalendars = props.postfaecher;

    const shellProps: CalendarShellProps = {
        modus,
        onPick: props.onPick,
        bereitsVerknuepft: props.bereitsVerknuepft,
        neuTerminPrefill: props.neuTerminPrefill,
        titel: props.titel,
        hinweis: props.hinweis,
        windowId: props.windowId,
        imOverlay: props.imOverlay,
    };

    return (
        <CalendarStateProvider
            auswahlIsoliert={auswahlIsoliert}
            initialCalendars={initialCalendars}
            initialDate={props.initialDate}
            initialView={props.initialView}
        >
            {deepLink && <CalendarDeepLink />}
            {/* Scope `.cal-rd` auf der Root — zieht das calendar.css-Override. */}
            <div className="cal-rd" style={{ height: "100%", minHeight: 0 }}>
                {/* SP6: vollständige Mobile-Komposition (Tages-Selektor, Agenda,
                    kompakter Monat, FAB, Bottom-Sheets) statt des SP1-Platzhalters.
                    K6: BEIDE Shells bekommen dieselben Props — sonst griffe der
                    Auswahlmodus auf dem Telefon nicht. */}
                {isPhone ? (
                    <MobileCalendarShell {...shellProps} />
                ) : (
                    <DesktopShell {...shellProps} />
                )}
            </div>
        </CalendarStateProvider>
    );
};

export default CalendarView;
